import type { AppId } from "@/types";

/**
 * Real external app launch links. When a `VITE_*_URL` is configured, opening
 * that app from the Hive desktop launches the actual product (which round-trips
 * through Hive SSO) instead of the in-desktop mock window. Unset ⇒ the mock
 * window keeps working, so the desktop demo is never broken by missing env.
 */

const EXTERNAL_APP_URLS: Partial<Record<AppId, string | undefined>> = {
  ares: import.meta.env.VITE_ARES_URL,
  athena: import.meta.env.VITE_ATHENA_URL,
  byonc: import.meta.env.VITE_BYONC_URL,
  garlic: import.meta.env.VITE_GARLIC_URL,
  appgraph: import.meta.env.VITE_APPGRAPH_URL,
};

/** Configured external URL for an app, or null (⇒ use the in-desktop window). */
export function externalAppUrl(appId: AppId): string | null {
  const url = EXTERNAL_APP_URLS[appId];
  return url ? url.replace(/\/$/, "") : null;
}

/** ARES deep link that tells the IDE which Hive workspace to open. */
export function aresWorkspaceUrl(workspaceId: string): string | null {
  const base = externalAppUrl("ares");
  if (!base) return null;
  return `${base}/?hive_workspace=${encodeURIComponent(workspaceId)}`;
}

/**
 * Open an external app in a new tab without leaking an opener handle.
 * `src=hive` tells the product this visit comes from the Hive desktop (a live
 * Hive session exists), so it can skip its welcome screen and sign in silently.
 */
export function openExternalApp(url: string): void {
  const target = new URL(url);
  target.searchParams.set("src", "hive");
  window.open(target.toString(), "_blank", "noopener,noreferrer");
}
